import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { applyPageMetadata, applyStructuredData } from '../utils/seo';

const faqs = [
  {
    question: 'How does BarsConnect know where I am located?',
    answer: 'You add location details to your profile so bar owners can discover you when searching nearby talent.',
  },
  {
    question: 'Can I set a travel radius?',
    answer: 'You can provide your location preferences on your profile. Specific distance preference behavior may vary by current profile fields.',
  },
  {
    question: 'Can I mention multiple neighborhoods or cities?',
    answer: 'You can describe where you are available to work in your profile details so bars can quickly assess fit.',
  },
  {
    question: 'Are there fees to use BarsConnect?',
    answer: 'No. BarsConnect is currently free for bartenders during beta, with no credit card required for signup.',
  },
  {
    question: 'Will my exact address be publicly displayed?',
    answer: 'Profiles show location information intended to help relevant bars find you. Keep profile details aligned with your comfort level.',
  },
];

export default function BartenderJobsNearMePage() {
  useEffect(() => {
    const cleanupMeta = applyPageMetadata({
      title: 'Bartender Jobs Near Me — Connect With Local Bars | BarsConnect',
      description: 'Find bartender jobs near you on BarsConnect. Create a free profile, set your location, and connect with bar owners hiring in your area. Free beta access — no credit card needed.',
      keywords: 'bartender jobs near me, local bartender jobs, bartending shifts nearby, bar jobs near me',
      canonicalUrl: 'https://barsconnect.com/bartender-jobs-near-me',
      robots: 'index,follow',
    });

    const cleanupSchema = applyStructuredData([
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://barsconnect.com/' },
          { '@type': 'ListItem', position: 2, name: 'Bartender Jobs', item: 'https://barsconnect.com/bartender-jobs' },
          { '@type': 'ListItem', position: 3, name: 'Bartender Jobs Near Me', item: 'https://barsconnect.com/bartender-jobs-near-me' },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqs.map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.answer,
          },
        })),
      },
    ]);

    return () => {
      cleanupMeta();
      cleanupSchema();
    };
  }, []);

  return (
    <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8" data-testid="bartender-jobs-near-me-page">
      <div className="max-w-5xl mx-auto">
        <header className="mb-10" data-testid="bartender-jobs-near-me-hero-section">
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4" data-testid="bartender-jobs-near-me-title">
            Bartender Jobs Near You — Connect With Bars in Your Area
          </h1>
          <p className="text-muted-foreground mb-4" data-testid="bartender-jobs-near-me-hero-copy-1">
            Bartending is local by nature. BarsConnect helps you present your location and availability so bars in your area can identify you faster.
          </p>
          <p className="text-muted-foreground mb-6" data-testid="bartender-jobs-near-me-hero-copy-2">
            Create a free profile, keep your details accurate, and connect with nearby opportunities that fit your schedule.
          </p>
          <Button asChild data-testid="bartender-jobs-near-me-primary-cta-btn">
            <Link to="/register/bartenders">Create Free Bartender Profile</Link>
          </Button>
        </header>

        <section className="mb-10" data-testid="bartender-jobs-near-me-how-it-works-section">
          <h2 className="text-2xl font-semibold text-white mb-4" data-testid="bartender-jobs-near-me-how-it-works-heading">How It Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="bg-white/5 border border-white/10" data-testid="bartender-jobs-near-me-step-1-card">
              <CardContent className="p-5">
                <h3 className="text-white font-semibold mb-2" data-testid="bartender-jobs-near-me-step-1-title">Step 1 — Set Your Location</h3>
                <p className="text-sm text-muted-foreground" data-testid="bartender-jobs-near-me-step-1-copy">Add location details to your profile so nearby bars can find your profile in local searches.</p>
              </CardContent>
            </Card>
            <Card className="bg-white/5 border border-white/10" data-testid="bartender-jobs-near-me-step-2-card">
              <CardContent className="p-5">
                <h3 className="text-white font-semibold mb-2" data-testid="bartender-jobs-near-me-step-2-title">Step 2 — Get Seen by Local Bars</h3>
                <p className="text-sm text-muted-foreground" data-testid="bartender-jobs-near-me-step-2-copy">Bar owners can browse local bartender profiles and review experience, skills, and availability.</p>
              </CardContent>
            </Card>
            <Card className="bg-white/5 border border-white/10" data-testid="bartender-jobs-near-me-step-3-card">
              <CardContent className="p-5">
                <h3 className="text-white font-semibold mb-2" data-testid="bartender-jobs-near-me-step-3-title">Step 3 — Connect Directly</h3>
                <p className="text-sm text-muted-foreground" data-testid="bartender-jobs-near-me-step-3-copy">When a bar is interested, messaging starts directly in-platform so you can evaluate fit quickly.</p>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className="mb-10" data-testid="bartender-jobs-near-me-location-matters-section">
          <h2 className="text-2xl font-semibold text-white mb-3" data-testid="bartender-jobs-near-me-location-matters-heading">Why Location Matters in Bartending</h2>
          <p className="text-muted-foreground mb-4" data-testid="bartender-jobs-near-me-location-matters-copy-1">
            Commute, neighborhood familiarity, and shift timing all influence whether a role is truly workable. Local-fit opportunities are often more practical and sustainable.
          </p>
          <p className="text-muted-foreground" data-testid="bartender-jobs-near-me-location-matters-copy-2">
            If you are also open to broader roles, explore <Link className="text-primary hover:underline" to="/bartender-jobs" data-testid="bartender-jobs-near-me-inline-link-general">bartender jobs</Link>,
            {' '}<Link className="text-primary hover:underline" to="/part-time-bartender-jobs" data-testid="bartender-jobs-near-me-inline-link-part-time">part-time shifts</Link>, and
            {' '}<Link className="text-primary hover:underline" to="/event-bartender-jobs" data-testid="bartender-jobs-near-me-inline-link-event">event opportunities</Link>.
          </p>
        </section>

        <section className="mb-10" data-testid="bartender-jobs-near-me-faq-section">
          <h2 className="text-2xl font-semibold text-white mb-4" data-testid="bartender-jobs-near-me-faq-heading">Frequently Asked Questions</h2>
          <div className="space-y-3">
            {faqs.map((item, index) => (
              <Card key={item.question} className="bg-white/5 border border-white/10" data-testid={`bartender-jobs-near-me-faq-card-${index + 1}`}>
                <CardContent className="p-5">
                  <h3 className="text-white font-semibold mb-2" data-testid={`bartender-jobs-near-me-faq-question-${index + 1}`}>{item.question}</h3>
                  <p className="text-sm text-muted-foreground" data-testid={`bartender-jobs-near-me-faq-answer-${index + 1}`}>{item.answer}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        <section data-testid="bartender-jobs-near-me-repeat-cta-section">
          <Card className="bg-white/5 border border-white/10" data-testid="bartender-jobs-near-me-repeat-cta-card">
            <CardContent className="p-6 sm:p-8">
              <h2 className="text-2xl font-semibold text-white mb-3" data-testid="bartender-jobs-near-me-repeat-cta-heading">Put your profile in front of local bars.</h2>
              <p className="text-muted-foreground mb-5" data-testid="bartender-jobs-near-me-repeat-cta-copy">Create your free profile and stay visible for opportunities in your area.</p>
              <div className="flex flex-wrap gap-3">
                <Button asChild data-testid="bartender-jobs-near-me-repeat-cta-register-btn">
                  <Link to="/register/bartenders">Create Free Bartender Profile</Link>
                </Button>
                <Button asChild variant="outline" data-testid="bartender-jobs-near-me-repeat-cta-app-btn">
                  <Link to="/bartender-app">Learn About the Bartender App Experience</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </section>
      </div>
    </div>
  );
}
